import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Highlight,
  IconButton,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { GATEWAYS, METERS_TO_MILES } from '@pelicargo/utils';
import { cloneDeep } from 'lodash';
import { ReactNode, RefObject, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { HiCheck, HiOutlinePlus, HiOutlineStar, HiStar } from 'react-icons/hi';
import { InstantSearch, useHits } from 'react-instantsearch-core';
import usePlacesAutocomplete, { getGeocode, getLatLng, Suggestion } from 'use-places-autocomplete';

import { useFavoriteAirports } from '../../hooks/airports/useFavoriteAirports';
import { useTypesense } from '../../hooks/useTypesense';
import { Search } from './Search';

type Address = {
  name: string;
  latitude: number;
  longitude: number;
  country: string;
  place_id: string;
};

type HitProps = {
  selectedAddress: Address;
  isFavorite: (airportId: string) => boolean;
  renderHit: (hit: any) => ReactNode;
};

const Hits = ({ selectedAddress, isFavorite, renderHit }: HitProps) => {
  const { hits = [] } = useHits();

  const compareHits = useCallback(
    (a, b) => {
      const aIsGateway = GATEWAYS.includes(a.id);
      const bIsGateway = GATEWAYS.includes(b.id);

      if (aIsGateway && !bIsGateway) {
        return -1;
      } else if (!aIsGateway && bIsGateway) {
        return 1;
      }

      const addrDiff = selectedAddress
        ? a.geo_distance_meters?.airport_latlng - b.geo_distance_meters?.airport_latlng
        : 0;
      if (addrDiff) return addrDiff;

      if (aIsGateway && bIsGateway) return GATEWAYS.indexOf(b.id) - GATEWAYS.indexOf(a.id);

      const favDiff = Number(isFavorite(b.id)) - Number(isFavorite(a.id));
      if (favDiff) return favDiff;
    },
    [selectedAddress, isFavorite],
  );

  const sortedHits = cloneDeep(hits).sort(compareHits);

  return <Stack>{sortedHits.slice(0, 11).map(renderHit)}</Stack>;
};

type AirportPickerProps = {
  isOrigin?: boolean;
  allowAll?: boolean; // Shows international hits
  maxSelected?: number;
  onClose: () => void;
  fieldName?: string;
  initialFocusRef?: RefObject<HTMLInputElement>;
};

export const AirportPicker = ({
  isOrigin = false,
  allowAll = false,
  maxSelected = 1,
  onClose,
  fieldName,
  initialFocusRef,
}: AirportPickerProps) => {
  const { setValue, watch } = useFormContext();
  const [selectedAddress, setSelectedAddress] = useState<Address>();
  const [searchByAddress, setSearchByAddress] = useState(false);
  const { handleFavoriteClicked, isFavorite, pinned_hits } = useFavoriteAirports();
  const { suggestions, value: query, setValue: setQuery } = usePlacesAutocomplete({ debounce: 300 });

  const isArray = useMemo(() => maxSelected !== 1, [maxSelected]);

  const name = useMemo(
    () => fieldName ?? (isOrigin ? 'origin_airports' : 'destination_airports'),
    [isOrigin, fieldName],
  );

  const filter_by = useMemo(() => {
    const optionalFilters = [];

    if (selectedAddress) {
      optionalFilters.push(`airport_latlng:(${selectedAddress?.latitude}, ${selectedAddress?.longitude}, 2000 mi)`);
    }

    if (!allowAll) {
      optionalFilters.push(isOrigin ? 'airport_country:=US' : 'airport_country:!=US');
    }

    return optionalFilters.filter(Boolean).join(' && ');
  }, [selectedAddress, isOrigin, allowAll]);

  const sort_by = useMemo(() => {
    const baseSort = 'airport_main_gateway:desc,airport_';

    if (selectedAddress) {
      return baseSort + `latlng(${selectedAddress?.latitude}, ${selectedAddress?.longitude}):asc`;
    }

    return baseSort + (isOrigin ? 'origin' : 'destination') + '_popularity:desc';
  }, [selectedAddress, isOrigin]);

  const additionalSearchParameters = useMemo(
    () => ({
      query_by: 'airport_id, airport_name',
      sort_by,
      per_page: 20,
      filter_by,
      pinned_hits,
    }),
    [sort_by, filter_by, pinned_hits],
  );

  const typesense = useTypesense({ additionalSearchParameters });

  const selectedAirports = watch(name) || (isArray ? [] : []);
  const [isMultiSelectRecord, setIsMultiSelect] = useState<Record<string, boolean>>({
    [name]: selectedAirports?.length > 1,
  });
  const isMultiSelect = isMultiSelectRecord[name] || false;

  const deselectAddress = () => setSelectedAddress(null);

  const isSelected = useCallback(
    (airportId: string) => (isArray ? selectedAirports.includes(airportId) : selectedAirports === airportId),
    [isArray, selectedAirports],
  );

  const handleListItemClick = useCallback(
    (airport: string) => () => {
      setQuery('');

      // Deselect
      if (isSelected(airport)) {
        setValue(name, isArray ? selectedAirports?.filter((item) => item !== airport) : '');
        return;
      }

      // Select
      setValue(name, isArray ? (isMultiSelect ? [...selectedAirports, airport] : [airport]) : airport);
      deselectAddress();
      setSearchByAddress(false);
      setQuery('');
      if (!isMultiSelect) onClose();
    },
    [setQuery, isSelected, setValue, name, isArray, isMultiSelect, selectedAirports, onClose],
  );

  const handlePredictionChange = (prediction: Suggestion) => async () => {
    const name = prediction?.description;

    if (name === selectedAddress?.name) {
      deselectAddress();
      return;
    }

    const result = (await getGeocode({ address: name }))?.[0];
    const { lat: latitude, lng: longitude } = getLatLng(result);

    setSelectedAddress({
      place_id: result?.place_id,
      name,
      latitude,
      longitude,
      country: result.address_components?.find(({ types }) => types?.includes('country'))?.short_name ?? '',
    });
  };

  const renderPrediction = (prediction: Suggestion) => (
    <Checkbox
      key={prediction?.place_id}
      size="md"
      colorScheme="primary"
      whiteSpace="nowrap"
      onChange={handlePredictionChange(prediction)}
    >
      {prediction?.description}
    </Checkbox>
  );

  const renderAirport = useCallback(
    (airport: string) =>
      selectedAirports.length ? (
        <WrapItem key={airport}>
          <Tag size="sm" borderRadius="full" variant="outline" color="offBlack">
            <TagLabel>{airport}</TagLabel>
            <TagCloseButton onClick={handleListItemClick(airport)} />
          </Tag>
        </WrapItem>
      ) : null,
    [selectedAirports, handleListItemClick],
  );

  const renderHit = useCallback(
    (hit) => {
      const airportId = hit?.airport_id;
      const isChecked = isSelected(airportId);

      if (!allowAll && !isOrigin === (hit?.airport_country === 'US')) return; // Ensures that search type and airport type match (either both origin or both destination) (effectively a logical xor)
      if (!hit?.airport_main_gateway && !isChecked && !isFavorite(airportId) && !query) return;
      if (hit?.geo_distance_meters?.airport_latlng * METERS_TO_MILES > 500) return;

      const distance = Math.round((hit?.geo_distance_meters?.airport_latlng || 0) * METERS_TO_MILES);
      // This works because of a hack. If selectedAirports is an array, it functions as expected.
      // If it is a string, then maxSelected is 1, so a selected airport (string length 3) puts it over the max while nonselected is still length 0.
      const isDisabled = selectedAirports.length >= maxSelected && !isChecked;

      return (
        <Stack direction="row" align="center" key={airportId}>
          <Tooltip
            label={
              <Stack align="center" gap={0}>
                <Text>{`A total of ${maxSelected} station${isArray ? 's' : ''} allowed`}</Text>
                <Text>Please remove one before selecting another</Text>
              </Stack>
            }
            isDisabled={!isDisabled}
            hasArrow
            closeDelay={0}
          >
            <Box as="span" display="flex" flex="1" alignItems="center" w="100%">
              <Checkbox
                size="sm"
                w="full"
                colorScheme="primary"
                fontWeight={hit?.airport_main_gateway ? 'bold' : 'unset'}
                color={isChecked ? 'text.default' : 'text.subtle'}
                pointerEvents="auto"
                sx={{
                  '.chakra-checkbox__label': {
                    width: '100%',
                  },
                  '> span:first-of-type': {
                    display: isMultiSelect ? 'inline-flex' : 'none',
                  },
                }}
                isDisabled={isDisabled}
                isChecked={isChecked}
                onChange={handleListItemClick(airportId)}
              >
                <Stack direction="row" align="center" justify="space-between" spacing={4} w="full">
                  <Show if={searchByAddress}>
                    <Text w="fit-content" minW="50px" whiteSpace="nowrap">
                      {distance ? `${distance.toFixed(0)} mi` : ''}
                    </Text>
                  </Show>
                  <Text whiteSpace="nowrap" flex={10} noOfLines={1}>
                    <Highlight query={query} styles={{ fontWeight: 'bold' }}>
                      {hit?.airport_name}
                    </Highlight>
                  </Text>
                  <Text fontSize="sm" flex={1} textAlign="right">
                    <Highlight query={query} styles={{ fontWeight: 'bold' }}>
                      {airportId}
                    </Highlight>
                  </Text>
                </Stack>
              </Checkbox>
            </Box>
          </Tooltip>
          <IconButton
            icon={isFavorite(airportId) ? <HiStar /> : <HiOutlineStar />}
            aria-label={`Favorite ${airportId}`}
            variant="unstyled"
            color="primary.500"
            colorScheme="primary"
            size="sm"
            height="fit-content"
            onClick={handleFavoriteClicked(airportId)}
          />
        </Stack>
      );
    },
    [
      isSelected,
      allowAll,
      isOrigin,
      isFavorite,
      query,
      selectedAirports.length,
      maxSelected,
      isArray,
      isMultiSelect,
      handleListItemClick,
      searchByAddress,
      handleFavoriteClicked,
    ],
  );

  if (!typesense?.searchClient) return 'Loading ...';

  return (
    <InstantSearch searchClient={typesense?.searchClient} indexName="airports">
      <Stack w="full" maxW="none" pb="4">
        <Wrap>{isArray ? selectedAirports.map(renderAirport) : renderAirport(selectedAirports)}</Wrap>
        <ButtonGroup size="sm">
          <Button variant={!searchByAddress ? 'underline' : 'unselected'} onClick={() => setSearchByAddress(false)}>
            by Airport
          </Button>
          <Button variant={searchByAddress ? 'underline' : 'unselected'} onClick={() => setSearchByAddress(true)}>
            by Address
          </Button>
        </ButtonGroup>
        <Stack direction="row" justify="space-between" w="full" pr={2}>
          <Search
            query={query}
            placeholder={isOrigin ? 'Where from?' : 'Where to?'}
            refineSearch={!searchByAddress}
            setQuery={setQuery}
            initialFocusRef={initialFocusRef}
          />
          <Show if={isArray}>
            <Show if={!isMultiSelect}>
              <Tooltip label="Select multiple airports" hasArrow>
                <Button
                  leftIcon={<HiOutlinePlus />}
                  size="xs"
                  minW="min-content"
                  onClick={() => setIsMultiSelect((prev) => ({ ...prev, [name]: true }))}
                >
                  Multiple Airports
                </Button>
              </Tooltip>
            </Show>
            <Show if={isMultiSelect}>
              <Tooltip label="Switch to single airport selection" hasArrow>
                <Button
                  leftIcon={<HiCheck />}
                  size="xs"
                  minW="min-content"
                  onClick={() => {
                    setValue(name, [selectedAirports?.[0]]);
                    setIsMultiSelect((prev) => ({ ...prev, [name]: false }));
                  }}
                >
                  Single Airport
                </Button>
              </Tooltip>
            </Show>
          </Show>
        </Stack>
        <Show if={searchByAddress}>
          <Show if={!suggestions?.data?.length}>
            <Text fontSize="sm" color="text.placeholder" w="md">
              {query ? 'No results found' : 'Search by address, city, or zip'}
            </Text>
          </Show>
          <Show if={!selectedAddress}>{suggestions?.data?.map(renderPrediction)}</Show>
          <Show if={!!selectedAddress}>
            <Checkbox
              key={selectedAddress?.place_id}
              size="md"
              colorScheme="primary"
              isChecked
              whiteSpace="nowrap"
              onChange={deselectAddress}
            >
              {selectedAddress?.name}
            </Checkbox>
            <Divider orientation="horizontal" />
          </Show>
        </Show>
        <Show if={!searchByAddress || !!selectedAddress}>
          <Hits selectedAddress={selectedAddress} isFavorite={isFavorite} renderHit={renderHit} />
        </Show>
      </Stack>
    </InstantSearch>
  );
};
